import Avatar from "@galaxy-io/dls/avatar/Avatar";
import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import Flex, { AlignItems, FlexDirection, FlexWrap } from "@galaxy-io/dls/layout/Flex";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Widget, { WidgetSize } from "@galaxy-io/dls/widget/Widget";
import { styled } from "@linaria/react";
import { ArrowRightIcon, BuildingsIcon, PlusIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/workspace/page-header";
import { SelectField } from "@/components/workspace/select-field";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { apiClient, unwrap } from "@/lib/api-client";
import { DreamMapView } from "./dream-map";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  align-content: start;
  gap: ${t.space[16]};
  padding: ${t.space[16]};
  overflow: auto;
`;
export function DreamListView() {
  const { params, update } = useUrlState();
  const selected = params.get("target");
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const targets = useQuery({
    queryKey: ["dream-list"],
    queryFn: ({ signal }) => unwrap(apiClient().GET("/dream-list", { signal })),
  });
  const items = (targets.data ?? []).filter((item) =>
    [item.name, item.domain, item.notes].join(" ").toLowerCase().includes(search.toLowerCase()),
  );
  if (selected)
    return <DreamMapView key={selected} id={selected} onBack={() => update({ target: null })} />;
  return (
    <>
      <PageHeader
        title="Dream List"
        description="The companies you want to know. The people who can get you there."
        actions={
          <Button
            label="Add company"
            icon={PlusIcon}
            size={ButtonSize.SMALL}
            onClick={() => setAdding(true)}
          />
        }
      />
      <BaseToolbar
        leadingActions={
          <Input
            label="Search dream list"
            hideLabel
            type="search"
            size={InputSize.SMALL}
            placeholder="Search companies…"
            value={search}
            onChange={setSearch}
          />
        }
      />
      {targets.isPending || targets.error ? (
        <QueryState error={targets.error} onRetry={() => void targets.refetch()} />
      ) : !items.length ? (
        <EmptyState
          icon={BuildingsIcon}
          header={search ? "No companies match" : "Start with a company you’d love to win"}
          description={
            search
              ? "Try another company name or domain."
              : "Add a company, map the people you know, and leave room for the roles you still need to find."
          }
          actions={
            !search && (
              <Button
                variant={ButtonVariant.SECONDARY}
                label="Choose your first company"
                onClick={() => setAdding(true)}
              />
            )
          }
        />
      ) : (
        <Grid>
          {items.map((item) => (
            <Widget
              key={item.id}
              size={WidgetSize.LARGE}
              header={
                <Flex gap={12} alignItems={AlignItems.CENTER}>
                  <Avatar name={item.name} isSquare />
                  <Flex direction={FlexDirection.COLUMN} gap={4} minWidth={0}>
                    <Text
                      size={TextSize.BODY_LG}
                      weight={TextWeight.MEDIUM}
                      lineClamp={1}
                      shouldTooltipOnOverflow
                    >
                      {item.name}
                    </Text>
                    <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={1}>
                      {item.domain || "Company target"}
                    </Text>
                  </Flex>
                </Flex>
              }
              footer={
                <Button
                  label={`Open ${item.name} map`}
                  icon={ArrowRightIcon}
                  isIconTrailing
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.SECONDARY}
                  onClick={() => update({ target: item.id })}
                />
              }
            >
              <Flex direction={FlexDirection.COLUMN} gap={16}>
                <Flex gap={24} wrap={FlexWrap.WRAP}>
                  <Flex direction={FlexDirection.COLUMN} gap={4}>
                    <Text size={TextSize.HEADING_MD} isTabular>
                      {item.peopleCount}
                    </Text>
                    <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                      People mapped
                    </Text>
                  </Flex>
                  <Flex direction={FlexDirection.COLUMN} gap={4}>
                    <Text size={TextSize.HEADING_MD} isTabular>
                      {item.placeholderCount}
                    </Text>
                    <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                      Roles to find
                    </Text>
                  </Flex>
                </Flex>
                <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={2}>
                  {item.notes || "Build a picture of the team and your next conversation."}
                </Text>
              </Flex>
            </Widget>
          ))}
        </Grid>
      )}
      {adding && (
        <AddCompany
          onClose={() => setAdding(false)}
          onAdded={(id) => {
            setAdding(false);
            update({ target: id });
          }}
        />
      )}
    </>
  );
}
function AddCompany({ onClose, onAdded }: { onClose: () => void; onAdded: (id: string) => void }) {
  const client = useQueryClient();
  const options = useQuery({
    queryKey: ["company-options"],
    queryFn: ({ signal }) => unwrap(apiClient().GET("/companies/options", { signal })),
  });
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <SurfaceDialog
      open
      onClose={onClose}
      title="Add a dream company"
      dirty={!!companyId}
      busy={busy}
      headerDescription="Choose a company from your directory. Its people and profile stay connected."
      footer={
        <Button
          label="Add to Dream List"
          isDisabled={!companyId}
          isLoading={busy}
          onClick={async () => {
            setBusy(true);
            setError(undefined);
            try {
              const result = await unwrap(apiClient().POST("/dream-list", { body: { companyId } }));
              void client.invalidateQueries({ queryKey: ["dream-list"] });
              onAdded(result.id);
            } catch (reason) {
              setError((reason as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        />
      }
    >
      {options.isPending || options.error ? (
        <QueryState error={options.error} onRetry={() => void options.refetch()} />
      ) : (
        <SelectField
          label="Company"
          search
          value={companyId}
          onChange={setCompanyId}
          placeholder="Search companies"
          options={options.data.map((company) => ({ value: company.id, label: company.name }))}
        />
      )}
      {options.data?.length === 0 && <Text>Add a company in the Companies directory first.</Text>}
      {error && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
    </SurfaceDialog>
  );
}
